import type { Dispatch, SetStateAction } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import type {
    ActivatedChildComponentType,
    TableActionProps,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    SaveToListButtonContainer,
    selectActiveLeadsType,
    AssignToUserButtonContainer,
} from "app/pages/sales-intelligence/modules/leads-search";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import listTrackingService from "app/pages/sales-intelligence/modules/lists/services/tracking/trackingService";
import {
    opportunityContactsQueryKeys,
    opportunityListQueryKeyFactory,
} from "app/pages/sales-intelligence/pages/opportunity-list/query/queryKeyFactory";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import { tableActionsI18KeyModifier } from "app/pages/sales-intelligence/sub-modules/opportunities/configuration/tableActions";
import { selectIsRecentlyViewedOpportunityList } from "app/pages/sales-intelligence/sub-modules/opportunities/store/selectors";

import AssignToUserPanelContainer from "../AssignToUserPanel/AssignToUserPanelContainer";
import MoveToListPanelContainer from "../MoveToListPanel/MoveToListPanelContainer";
import RemoveFromListButtonContainer from "../RemoveFromListButton/RemoveFromListButtonContainer";

const StaticListTableActionsContainer = ({ isDisabled = false, onActivate }: TableActionProps) => {
    const queryClient = useQueryClient();

    const listParams = useListParams();

    const activeLeadsType = useSelector(selectActiveLeadsType);
    const isRecentlyViewedList = useSelector((state) => selectIsRecentlyViewedOpportunityList(state, listParams.id));

    const isChangeable = !isRecentlyViewedList;

    const handleStayOnPage = (): void => {
        trackingService.trackMultiSelectorSuccessMovedPopup("stay on this page");

        void queryClient.invalidateQueries({
            queryKey: opportunityListQueryKeyFactory.leadsTableData(listParams.id),
        });
        void queryClient.invalidateQueries({
            queryKey: opportunityContactsQueryKeys.tableData(listParams.id),
        });
        void queryClient.invalidateQueries({
            queryKey: opportunityListQueryKeyFactory.appsTableData(listParams.id),
        });
    };

    const handleGoToList = () => trackingService.trackMultiSelectorSuccessMovedPopup("go to list");

    const handleAssignToUser = (activate: Dispatch<SetStateAction<ActivatedChildComponentType>>): void => {
        trackingService.trackMultiSelectorAssignToOtherUserClick();

        return activate(() => AssignToUserPanelContainer);
    };

    const handleSaveToList = (activate: Dispatch<SetStateAction<ActivatedChildComponentType>>): void => {
        trackingService.trackMultiSelectorMoveToListClick();

        return activate(() => MoveToListPanelContainer);
    };

    return (
        <>
            {isRecentlyViewedList ? null : (
                <SaveToListButtonContainer
                    isDisabled={isDisabled}
                    i18nKeyModifier={tableActionsI18KeyModifier[activeLeadsType]}
                    iconName="arrow-right"
                    onClick={() => handleSaveToList(onActivate)}
                    onStayOnPage={handleStayOnPage}
                    onGoToList={handleGoToList}
                    onActivate={onActivate}
                    onHookClick={() => listTrackingService.trackMoveToListHookClick()}
                />
            )}
            <AssignToUserButtonContainer
                isDisabled={isDisabled}
                onClick={() => handleAssignToUser(onActivate)}
                onActivate={onActivate}
            />

            {isChangeable ? (
                <RemoveFromListButtonContainer
                    isDisabled={isDisabled}
                    onActivate={onActivate}
                />
            ) : null}
        </>
    );
};

export default StaticListTableActionsContainer;
